import React from "react";
import { Button } from "../../../components/ui";
import { formatBillingDate } from "../billingDates";

interface CancelPlanCardProps {
  /** The day the cycle already paid for runs to, ISO. */
  paidUntil: string | null;
  onCancel: () => void;
}

/**
 * The way out, at the foot of Plan details.
 *
 * There from the day the plan is bought. It used to share this place with the
 * seven-day money back — `RefundRequestCard` held it while the window was open
 * and this card only took over once the window had shut, carrying the day it
 * closed so the promise was still accounted for. Refunds are not asked for from
 * this screen any more, so there is nothing to hand over and nothing to date: a
 * subscriber finds the same card in the same place on their second day as on
 * their second year.
 *
 * Which is also why cancelling is not up beside the plan. There it was the
 * second thing read about something just paid for; here it is the last, and a
 * workspace that scrolls past its own invoices to reach it is a workspace that
 * meant to.
 *
 * The button opens `CancelSubscriptionModal`, which is where the end of the
 * paid cycle is set out. Pressing this cancels nothing on its own.
 */
const CancelPlanCard: React.FC<CancelPlanCardProps> = ({
  paidUntil,
  onCancel,
}) => (
  <section
    aria-labelledby="cancel-plan-heading"
    className="rounded-md border border-line bg-white"
  >
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 3xl:px-6 3xl:py-5">
      <div className="min-w-0">
        <h2
          id="cancel-plan-heading"
          className="text-[15px] 3xl:text-[16px] font-medium text-ink"
        >
          Cancel plan
        </h2>
        <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
          {formatBillingDate(paidUntil) ? (
            <>
              Your plan stops renewing and you keep everything until{" "}
              {formatBillingDate(paidUntil)}.
            </>
          ) : (
            <>
              Your plan stops renewing and you keep everything until the end of
              the cycle you have paid for.
            </>
          )}
        </p>
      </div>

      <Button variant="dangerQuiet" size="sm" onClick={onCancel}>
        Cancel plan
      </Button>
    </div>
  </section>
);

export default CancelPlanCard;
